import { Component, EventEmitter, Input, Output } from '@angular/core';

/** Makes each rail's head id unique, so two rails on one screen never collide on `aria-labelledby`. */
let nextRailId = 0;

/**
 * The finder screens' persistent filter rail — the mockup's `div.card.rail`: a
 * sticky 252px white card holding a FILTERS head, the active-filter count, a
 * `Clear all` action, and whatever filter groups the page projects into it.
 *
 * It replaces `mj-filter-popover` on the finders. The popover hid every filter
 * behind a click and a CDK overlay capped at 45vh; the mockup keeps the whole
 * set on screen beside the results, which is the point of the design — the
 * member is meant to see what is filtered without opening anything.
 *
 * The rail deliberately knows nothing about WHAT it filters: each page projects
 * its own `ra-*-filters` component unchanged and wires `ClearAll` to its own
 * reset. `ActiveCount` is the same number the popover trigger used to badge.
 */
@Component({
    standalone: false,
    selector: 'agu-filter-rail',
    templateUrl: './filter-rail.component.html',
    styleUrl: './filter-rail.component.scss',
})
export class AGUFilterRailComponent {
    /** How many filter groups are narrowing the results right now. */
    @Input() ActiveCount = 0;

    @Output() ClearAll = new EventEmitter<void>();

    /** Ties the landmark to its own head, per instance. */
    readonly TitleId = `agu-rail-title-${(nextRailId += 1)}`;

    /** `Clear all` is live only when there is something to clear (mockup: it ships disabled). */
    get CanClear(): boolean {
        return this.ActiveCount > 0;
    }

    /**
     * Guarded rather than a bare `ClearAll.emit()` binding: the button is
     * disabled in the same state, and a page's clear handler re-runs the search,
     * so an unguarded emit from an unexpected path (a synthetic click, a future
     * keyboard shortcut) would cost a network round trip to change nothing.
     */
    RequestClearAll(): void {
        if (!this.CanClear) {
            return;
        }
        this.ClearAll.emit();
    }
}
